import React from 'react';
import useTheme from '@material-ui/core/styles/useTheme';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import styled, { ThemeProvider } from 'styled-components';
import { StylesProvider } from '@material-ui/core/styles';
import Paper from './Paper';
import T from 'i18n-react';
import Typography from '@material-ui/core/Typography';
import BackgroundImageAvatar from './BackgroundImageAvatar';
import Grid from '@material-ui/core/Grid';
import moment from 'moment';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { ArticleProps as BaseProps } from './props';
import SubjectSkeleton from './SubjectSkeleton';
import BodySkeletonList from './BodySkeletonList';
import ShareButtonGroup from '../Social/ShareButtonGroup';
import useMediaQuery from '@material-ui/core/useMediaQuery';

const userStyles = makeStyles((theme) => ({
  small: {
    fontSize: theme.typography.pxToRem(14),
  },
  subject: {
    margin: 0,
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 500,
    [theme.breakpoints.up('sm')]: {
      fontSize: theme.typography.pxToRem(27),
      display: 'flex',
      justifyContent: 'center',
    },
  },
  category: {
    lineHeight: 1,
    marginLeft: 10,
  },
}));

const Container = styled.div`
  ${({ theme }) => `
    & > *:nth-child(n + 2) {
      padding: ${theme.spacing(0, 2, 4)};
      margin: 0 auto;
    }
  `}
`;

type ArticleProps = Pick<
  BaseProps,
  'subject' | 'announcedAt' | 'backgroundImageUrl' | 'body',
> & {
  breadcrumbs?: React.ReactNode,
  categoryChip?: React.ReactNode,
  disableSns?: boolean,
  loaded?: boolean,
};

const SnsShare = ({ subject }) => {
  const currentUrl = window.location.href.split(/[?#]/)[0];
  const tweetText = `${subject}\n#おてつたび\n${currentUrl}`;
  const matches = useMediaQuery((theme) => theme.breakpoints.only('xs'));
  return (
    <Paper elevation={0}>
      <ShareButtonGroup
        loaded
        justify={matches ? 'center' : 'flex-end'}
        tweetText={tweetText}
        url={currentUrl}
      />
    </Paper>
  );
};

const Article = ({
  subject,
  announcedAt,
  backgroundImageUrl,
  body,
  breadcrumbs,
  categoryChip,
  disableSns,
  loaded,
}: ArticleProps) => {
  const classes = userStyles();

  const theme = useTheme();

  return (
    <MuiThemeProvider theme={theme}>
      <ThemeProvider theme={theme}>
        <StylesProvider injectFirst>
          <Container>
            {!!breadcrumbs && <Paper elevation={0}>{breadcrumbs}</Paper>}
            {!!backgroundImageUrl && (
              <Paper elevation={0}>
                <BackgroundImageAvatar loaded src={backgroundImageUrl} />
              </Paper>
            )}
            <Paper elevation={0}>
              <Grid container alignItems="center">
                <Grid item>
                  <Typography component="span" className={classes.small}>
                    {moment(announcedAt).format(T.translate('moment.date'))}
                  </Typography>
                </Grid>
                {!!categoryChip && (
                  <Grid item className={classes.category}>
                    {categoryChip}
                  </Grid>
                )}
              </Grid>
            </Paper>
            <Paper elevation={0}>
              {loaded ? (
                <Typography
                  variant="h2"
                  gutterBottom
                  className={classes.subject}
                >
                  <span>{subject}</span>
                </Typography>
              ) : (
                <SubjectSkeleton />
              )}
            </Paper>
            {!disableSns && <SnsShare subject={subject} />}
            <Paper elevation={0}>
              {loaded ? (
                <div
                  className="ck ck-content text-break-all"
                  dangerouslySetInnerHTML={{ __html: body }}
                />
              ) : (
                <BodySkeletonList />
              )}
            </Paper>
          </Container>
        </StylesProvider>
      </ThemeProvider>
    </MuiThemeProvider>
  );
};

export default Article;
